<template>
    <div class="settings__page clients__page--view" :class="{ 'disabled': paymentLinkLoading }">
        <div>
            <div class="revenue__goals__header">
                <div>
                    <FormLabel title="Payment & Accounts" html-for="info" label-type="tertiary" class="text--medium" />
                    <p class="text--normal text--sm text-gray-600">
                        Manage your payment accounts and links settings.
                    </p>
                </div>
            </div>
            
            <div class="mt--20">
                <div>
                    <div class="settings__auth__header mt--30 flex align-items-center justify-content-between">
                        <FormLabel title="Accounts" html-for="info" label-type="title" class="text--semi--bold mb--0" />
                        <BaseButton 
                            label="+ Add new account"
                            bold
                            variant="ghost"
                            class="p--0"
                            @submit="openAccountDetailsModal(null, 'add')"
                        />
                    </div>

                    <div>
                        <template v-if="!accounts.length">
                            <EmptyPage state="empty" type="account" icon-name="spreadsheet" />
                        </template>

                        <template v-else>
                            <ul class="settings__account__payment__list">
                                <li v-for="account in accounts" :key="account._id" class="revenue__goals__tasks__item">
                                    <BaseCard shadow style="overflow: visible;">
                                        <template #card-body>
                                            <div class="flex align-items-center justify-content-between">
                                                <p class="text--medium text--base text-gray-600">
                                                    {{ account.accountName }}
                                                </p>
                                                <div>
                                                    <div class="dropdown">
                                                        <BaseButton 
                                                            id="invoiceDetailsDropDown" 
                                                            data-bs-toggle="dropdown" 
                                                            aria-expanded="false"
                                                            prefix-icon
                                                            icon-name="dots-horizontal-rounded"
                                                            variant="secondary" 
                                                            variant-style="secondary"
                                                        />

                                                        <List id="statusDropdown">
                                                            <ListItem flex pointer @click="openAccountDetailsModal(account, 'edit')">
                                                                <IconSvg name="edit" fill="#64748B" width="16px" />
                                                                <span class="ml--10 text--sm">Edit</span>
                                                            </ListItem>
                                                            <ListItem flex pointer @click="deleteGoal(account._id)">
                                                                <IconSvg name="delete" fill="#e04060" width="16px" />
                                                                <span class="ml--10 text--sm text-error-400">Delete</span>
                                                            </ListItem>
                                                        </List>
                                
                                                        <ul class="dropdown-menu" aria-labelledby="statusDropdown">
                                                            <li class="dropdown-item dropdown--item flex align-items-center" @click="openAccountDetailsModal(account, 'edit')" />
                                                            <li class="dropdown-item dropdown--item flex align-items-center" @click="deleteGoal(account._id)" />
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <p class="text--sm text-gray-600 mt--5">
                                                    {{ account.bankName }}
                                                </p>
                                                <p class="text--sm text-gray-600 mt--5">
                                                    {{ account.accountNumber }}
                                                </p>
                                                <p class="text--sm text-gray-600 mt--5">
                                                    {{ account.iban }}
                                                </p>
                                                <p class="text--sm text-gray-600 mt--5">
                                                    {{ account.sortCode }}
                                                </p>
                                            </div>
                                        </template>
                                    </BaseCard>
                                </li>
                            </ul>
                        </template>
                    </div>
                </div>

                <!-- payment links -->
                <div>
                    <div class="settings__auth__header mt--30 flex align-items-center justify-content-between">
                        <FormLabel title="Payment links" html-for="info" label-type="title" class="text--semi--bold mb--0" />
                    </div>

                    <div class="col-12 flex align-items-end justify-content-between mt--20" :class="{ 'mb--25': !paymentLinks.length}">
                        <div class="form__row">
                            <div class="flex-1 mr--5">
                                <FormLabel title="Link name" html-for="info" />
                                <select v-model="paymentLinkForm.name" class="form-select form-select-sm text--sm filter-input text--capitalize">
                                    <option v-for="link in paymentLinkProviders" :key="link" :value="link">
                                        {{ link }}
                                    </option>
                                </select>
                            </div>

                            <FormInput 
                                id="linkUrl" 
                                v-model="paymentLinkForm.url"
                                size="sm" 
                                wrapper-class="flex-1"
                                info-type="required"
                                no-margin-bottom
                                html-for="linkUrl" 
                                label="Link URL"
                            />
                        </div>
                        
                        <BaseButton 
                            label="+ Add link"
                            :disabled="!paymentLinkForm.url || !paymentLinkForm.name"
                            :loading="paymentLinkLoading"
                            class="ml--10 max-h-fit"
                            @submit="handleUpdatePaymentLinks('add')"
                        />
                    </div>

                    <div>
                        <template v-if="!paymentLinks.length">
                            <EmptyPage state="empty" type="payment link" icon-name="link" />
                        </template>

                        <template v-else>
                            <div class="mt--40 mb--25">
                                <div 
                                    v-for="link in paymentLinks" 
                                    :key="link._id" 
                                    class="mb--10 flex align-items-center justify-content-between w-100 flex-wrap"
                                    :class="{ 'disabled': paymentLinkForm?._id === link._id }"
                                >
                                    <a class="project__item__title text--medium mt--0 text--sm text--underline" :href="link.url">
                                        {{ link.name }}
                                    </a>
                                    
                                    <div class="flex align-items-center">
                                        <BaseButton 
                                            variant="ghost" 
                                            label="Edit"
                                            medium
                                            class="mr--10 p--0"
                                            @submit="openPaymentLinkModal(link, 'update')"
                                        />
                                        <span class="text-gray-200">|</span>
                                        <BaseButton 
                                            variant="ghost" 
                                            label="Delete"
                                            variant-style="warning"
                                            :loading="btnLoadingState === 'delete_loading' && paymentLinkId === link._id"
                                            @submit="deleteLink(link._id)"
                                        />
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <ConfirmDeletion 
            type="card" 
            custom-msg 
            :show-modal="showAccountRemovalModal" 
            :custom-footer="true"
            @cancel="toggleDeleteInfoModal({ bool: false })" 
        >
            <template #custom-msg>
                <p class="text--sm text-gray-600">
                    You’re currently on a paid plan. To delete this card, please add a new card first.
                    <span class="text-label-info text-gray-800 text--xs text--semi--bold">
                        (This helps us retain an active card for your next billing cycle)
                    </span>
                </p>
            </template> 
            <template #custom-footer>
                <BaseButton 
                    label="Cancel" 
                    variant="outline" 
                    variant-style="secondary"
                    :loading="downgradeBtnLoading" 
                    @submit="showAccountRemovalModal = false"
                />
            </template>
        </ConfirmDeletion>

        <MainModal 
            :show-modal="showPaymentLinkModal"
            size="md" 
            title="Manage payment links" 
            @close="closePaymentLinkModal"
        >
            <template #body>
                <form @submit.prevent="">
                    <div class="form__row">
                        <div class="flex-1 mr--5">
                            <FormLabel title="Link name" html-for="info" />
                            <select v-model="paymentLinkForm.name" class="form-select form-select-sm text--sm filter-input text--capitalize">
                                <option v-for="link in paymentLinkProviders" :key="link" :value="link">
                                    {{ link }}
                                </option>
                            </select>
                        </div>
                        
                        <FormInput 
                            id="linkUrl" 
                            v-model="paymentLinkForm.url"
                            size="sm" 
                            wrapper-class="flex-1"
                            info-type="required"
                            no-margin-bottom
                            label="Link URL"
                        />
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        label="Cancel" 
                        variant="outline" 
                        :disabled="paymentLinkLoading" 
                        @submit="closePaymentLinkModal"
                    />
                    <BaseButton 
                        type="submit" 
                        :loading="paymentLinkLoading" 
                        :disabled="!paymentLinkForm.name || !paymentLinkForm.url" 
                        label="Save" 
                        @submit="handleUpdatePaymentLinks('update')"
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="showAccountDetailsModal"
            size="sm" 
            :title="accountDetailsModalType === 'add' ? 'Add new account' : 'Manage accounts'" 
            @close="closeAccountDetailsModal"
        >
            <template #body>
                <form @submit.prevent="">
                    <FormInput 
                        v-if="accountInfoByCurrency['bankName']"
                        id="bankName" 
                        v-model="accountDetails.bankName"
                        size="sm" 
                        info-type="required" 
                        label="Bank Name"
                    />
    
                    <FormInput 
                        v-if="accountInfoByCurrency['accountName']"
                        id="accountName" v-model="accountDetails.accountName"
                        size="sm" info-type="required" label="Account Name" 
                    />
                    <FormInput 
                        v-if="accountInfoByCurrency['accountNumber']"
                        id="accountNumber" v-model="accountDetails.accountNumber"
                        size="sm" info-type="required" label="Account Number"
                    />
    
                    <FormInput 
                        v-if="accountInfoByCurrency['iban']"
                        id="iban" v-model="accountDetails.iban"
                        size="sm" info-type="required" label="IBAN"
                    />
    
                    <FormInput 
                        v-if="accountInfoByCurrency['sortCode']"
                        id="sortCode" v-model="accountDetails.sortCode" 
                        class="mt--10 mb--0"
                        size="sm" info-type="required" 
                        label="Sort Code"
                    />
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        label="Cancel" 
                        variant="outline" 
                        :disabled="accountDetailsLoading" 
                        @submit="closeAccountDetailsModal"
                    />
                    <BaseButton 
                        type="submit" 
                        :loading="accountDetailsLoading" 
                        :disabled="disableAccountDetailsBtn" 
                        label="Update" 
                        @submit="handleUpdateAccountDetails"
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import {
    BaseCard,
    BaseButton,
    IconSvg,
    FormLabel,
    MainModal,
    ConfirmDeletion,
    FormInput,
    EmptyPage,
    List,
    ListItem,
} from "@/components/ui/index";

export default {
    name: "PaymentsAccounts",

    components: {
        BaseButton,
        BaseCard,
        IconSvg,
        FormLabel,
        MainModal,
        ConfirmDeletion,
        FormInput,
        EmptyPage,
        List,
        ListItem,
    },

    data() {
        return {
            paymentLinkForm: {
                url: "",
                name: "",
            },
            paymentLinkProviders: ["Paypal", "Stripe", "Flutterwave", "Paystack"],
            showPaymentLinkModal: false,
            paymentLinkLoading: false,
            accountDetailsLoading: false,
            accountDetails: {
                _id: "",
                accountName: "",
                bankName: "",
                accountNumber: "",
                iban: "",
                sortCode: "",
            },
            showAccountDetailsModal: false,
            paymentLinkModalType: "add",
            accountDetailsModalType: "add",
            showAccountRemovalModal: false,
            showPaymentLinkRemovalModal: false,
            accountId: "",
            paymentLinkId: "",
            btnLoadingState: "",
        };
    },

    computed: {
        subscriptionInfo() {
            return this.$store.getters["subscription/subscriptionInfo"];
        },

        accountPlan() {
            return this.subscriptionInfo?.plan || "free";
        },

        profile() {
            return this.$store.getters["auth/profile"];
        },

        accounts() {
            return this.profile.accounts || [];
        },

        paymentLinks() {
            return this.profile.paymentLinks || [];
        },

        currency() {
            return this.$store.getters["settings/general"]?.currency;
        },

        accountInfoByCurrency() {
            switch (this.currency.toLowerCase()) {
            case "ngn":
            case "ghs":
                return {
                    accountName: true,
                    bankName: true,
                    accountNumber: true,
                };
            case "usd":
            case "gbp":
            case "eur":
            case "usd":
                return {
                    accountName: true,
                    bankName: true,
                    accountNumber: true,
                    iban: true,
                };
            
            default:
                return null;
            }
        },

        disableAccountDetailsBtn() {
            return !this.accountDetails.accountName || !this.accountDetails.bankName || !this.accountDetails.accountNumber;
        },
    },

    methods: {
        toggleDeleteInfoModal({ bool, _id }) {
            this.showAccountRemovalModal = bool;
            this.accountId = _id;
        },

        async paymentSuccess(response) {
            try {
                this.paymentLinkLoading = true;

                const encryptedType = sessionStorage.getItem("z:t_type");
                const plan = sessionStorage.getItem("z:t_p");
                
                await this.$store.dispatch("subscription/verifyCharge", {
                    encryptedType,
                    reference: response.reference,
                    targetPlan: plan,
                });

                this.$toast.success("New card added");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            } finally {
                this.paymentLinkLoading = false;
            }
        },

        async handleUpdatePaymentLinks(type) {
            try {
                if (type === "add" && this.paymentLinks.length >= 5) {
                    this.$toast.error("Maximum of 5 payment links allowed");
                    return;
                }

                if (type === "update" && !this.paymentLinkForm._id) {
                    this.$toast.error("Please select a payment link to update");
                    return;
                }

                if (type === "add") {
                    delete this.paymentLinkForm._id;
                }

                this.paymentLinkLoading = true;
                
                await this.$store.dispatch("auth/updatePaymentLinks", { payload: this.paymentLinkForm, type: type || this.paymentLinkModalType });                 
                
                this.$toast.success("New payment link added");
                this.closePaymentLinkModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.paymentLinkLoading = false;
            }
        },

        async handleUpdateAccountDetails() {
            try {
                this.accountDetailsLoading = true;

                if (this.accountDetailsModalType === "add") {
                    delete this.accountDetails._id;
                }
                
                await this.$store.dispatch("auth/updatePaymentAccounts", { payload: this.accountDetails, type: this.accountDetailsModalType });                 
                
                this.$toast.success("Account added");
                this.closeAccountDetailsModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.accountDetailsLoading = false;
            }
        },

        openPaymentLinkModal(item, type) {
            if (type === "add" && this.paymentLinks.length >= 5) {
                this.$toast.error("You can only add a maximum of 5 payment links");
                return;
            }

            if (item) {
                this.paymentLinkForm = item;
            }
            this.this.paymentLinkModalType = type;
            this.showPaymentLinkModal = true;
        },

        closePaymentLinkModal() {
            this.paymentLinkForm = {
                name: "",
                link: "",
            };
            this.this.paymentLinkModalType = "";
            this.showPaymentLinkModal = false;
        },

        openAccountDetailsModal(item, type) {
            if (type === "add" && this.accounts.length >= 5) {
                this.$toast.error("You can only add a maximum of 5 accounts");
                return;
            }

            if (item) {
                this.accountDetails = item;
            } 

            this.accountDetailsModalType = type;
            this.showAccountDetailsModal = true;
        },

        closeAccountDetailsModal() {
            this.accountDetailsModalType = "";
            this.accountDetails = {
                _id: "",
                accountName: "",
                bankName: "",
                accountNumber: "",
                iban: "",
                sortCode: "",
            };
            this.showAccountDetailsModal = false;
        },

        async deleteLink(id) {            
            try {                                
                if (!id) {
                    this.$toast.error("Unable to delete link");
                    return;
                }

                this.paymentLinkId = id;
                this.btnLoadingState = "delete_loading";

                await this.$store.dispatch("auth/updatePaymentLinks", { type: "delete", payload: { _id: id }});        
            } catch (error) {
                const errMsg =error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.btnLoadingState = "";
            }
        },

    },
};
</script>